import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faCircleMinus } from '@fortawesome/pro-regular-svg-icons/faCircleMinus';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faCircleExclamationCheck } from "@fortawesome/pro-solid-svg-icons/faCircleExclamationCheck";
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardActions, CardContent, Grid, IconButton, MobileStepper, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcClickable, MprcContainer, MprcJoinComponents, MprcText, MprcTypography } from 'components';
import { CourseComponentEnableOption, EnrollmentComponentDto, EnrollStatus, MeetingType, TrainingComponentType } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    variant: "vertical" | "horizontal";
    items: EnrollmentComponentDto[];
    title?: string;
    readonly?: boolean;
    currentComponent?: EnrollmentComponentDto;
    onSelectComponent?: (component: EnrollmentComponentDto) => void;
}

////////////////////////////////////////////////////////////////////////////

const RenderPhase = (item: EnrollmentComponentDto) => {
    return (
        <Grid sx={{ mt: 1 }}>
            <MprcTypography variant="overline" noWrapRows={3}>{item.title}</MprcTypography>
        </Grid>
    );
};

////////////////////////////////////////////////////////////////////////////

const RenderScoGroup = (item: EnrollmentComponentDto) => {
    const { t } = useTranslation("lms");
    return (
        <Grid sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
                <MprcText
                    color={item.completed === true
                        ? ["success", "main"]
                        : ["muted", item.enabled ? "main" : "light"]
                    }
                >
                    <FontAwesomeIcon
                        icon={item.completed === true ? faCircleCheck : faCircle}
                        fontSize={16}
                        fixedWidth
                        title={
                            item.completed === true
                                ? t("Student.EnrollmentView.CompletedText")
                                : !item.enabled
                                    ? t("Student.EnrollmentView.Components.NotEnabledScoGroupText")
                                    : undefined
                        }
                    />
                </MprcText>
                <MprcTypography variant="overline" noWrapRows={3}>{item.title}</MprcTypography>
            </Stack>
        </Grid>
    );
};

////////////////////////////////////////////////////////////////////////////


interface IRenderTrainingComponent {
    props: IProps;
    item: EnrollmentComponentDto;
}

const RenderTrainingComponent = ({ item, props: { currentComponent, readonly, onSelectComponent } }: IRenderTrainingComponent) => {
    const { t } = useTranslation("lms");

    ///
    const BuildTitleTooltip = useCallback((item: EnrollmentComponentDto) => {

        if (!item.enabled) {
            switch (item.enableOption) {
                case CourseComponentEnableOption.WhenAllAboveCompleted:
                    return t("Student.EnrollmentView.Components.EnableWhenAllAboveCompletedText");
                case CourseComponentEnableOption.DaysAfterClassAvailability:
                    if (item.enableOn) return t("Student.EnrollmentView.Components.EnableDaysAfterClassAvailabilityText", { count: Formaters.dateDiffDays(item.enableOn) ?? 0 });
                    break;
                case CourseComponentEnableOption.WhenComponentListCompleted:
                    if (item.enableOnComponentsComplete) return t("Student.EnrollmentView.Components.EnableWhenComponentListCompletedText") + item.enableOnComponentsComplete.join(", ");

            }
        }

        return "";
    }, [t]);

    ///
    const BuildSubtitle = useCallback((item: EnrollmentComponentDto) => {

        if (item.componentType === TrainingComponentType.Post && item.postComponent) {
            return (
                <Typography variant="subtitle2" color="muted.light">
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <MprcJoinComponents
                            separator="&bull;"
                            components={[
                                item.postComponent.classificationName,
                                item.postComponent.mediaDuration != null && item.postComponent.mediaDuration > 0
                                    ? Formaters.secondsToFullTime(item.postComponent?.mediaDuration)
                                    : null
                            ]}
                        />
                    </Stack>
                </Typography>
            );
        }

        if (item.componentType == TrainingComponentType.Meeting && item.meetingComponent) {
            return (
                item.meetingComponent.isCancelled
                    ?
                    <Typography variant="subtitle2" component="span" textTransform="uppercase" color="error">{t("Student.EnrollmentView.Components.CanceledText")}</Typography>
                    :
                    <>
                        {item.meetingComponent.mandatoryAttendance && <Typography variant="subtitle2" component="span" textTransform="uppercase" color="warning.main">{t("Student.EnrollmentView.Components.IsMandatoryText")}</Typography>}
                        {item.meetingComponent.startDate && <Typography variant="subtitle2" color="muted.light">{Formaters.performaDate(item.meetingComponent.startDate, { dayOfWeek: true, preposition: "none", replaceClosestDays: true })}</Typography>}
                        <Typography variant="subtitle2" color="muted.light">{item.meetingComponent.meetingType != MeetingType.Presential ?
                            <>{t("Student.EnrollmentView.Components.OnlineText")}: {item.meetingComponent.meetingTypeName}</>
                            :
                            item.meetingComponent.locationName
                        }
                        </Typography>
                    </>
            );
        }

        if (item.componentType == TrainingComponentType.Activity && item.activityComponent && item.activityComponent.dueDate) {
            return (
                item.activityComponent.isCancelled
                    ? <Typography variant="subtitle2" textTransform="uppercase" color="error">{t("Student.EnrollmentView.Components.CanceledText")}</Typography>
                    : <Typography variant="subtitle2" color="muted.light">
                        {t("Student.EnrollmentView.Components.DeliveryActivityUntilText", { date: Formaters.performaDate(item.activityComponent.dueDate, { dayOfWeek: true, preposition: "none", replaceClosestDays: true }) })}
                    </Typography>
            );
        }

        if (item.componentType == TrainingComponentType.LearnPathTraining && item.trainingComponent) {

            var color = item.trainingComponent.status == EnrollStatus.Approved
                ? "success.main"
                : item.trainingComponent.status == EnrollStatus.Reproved
                    ? "error.main"
                    : "muted.light";

            return (
                <>
                    <Typography variant="subtitle2" color={color} textTransform="uppercase">
                        {
                            item.trainingComponent.enrollmentId == null ? t("Student.EnrollmentView.Components.NotEnrolledText") :
                                item.trainingComponent.status == EnrollStatus.Approved ? t("Student.EnrollmentView.ApprovedText")
                                    : item.trainingComponent.status == EnrollStatus.Reproved ? t("Student.EnrollmentView.ReprovedText")
                                        : !item.enabled ? t("Student.EnrollmentView.Components.NotEnabledText")
                                            : ""
                        }
                    </Typography>
                    <Typography variant="subtitle2" color={color}>
                        {
                            (item.trainingComponent.status == EnrollStatus.Approved || item.trainingComponent.status == EnrollStatus.Reproved) && item.trainingComponent.finalizationDate != null
                                ? ` ${t("Student.EnrollmentView.Components.FinalizedOnText", { date: Formaters.dayMonth(item.trainingComponent.finalizationDate) })}`
                                : (item.trainingComponent.enrollmentId != null && item.enabled)
                                    ? <>
                                        {t("Student.EnrollmentView.Components.ProgressText", { count: item.trainingComponent.progressPercent ?? 0 })}
                                        {`${item.trainingComponent.dueDate ? " - " + t("Student.EnrollmentView.Components.CompleteUntilText", { date: Formaters.dayMonth(item.trainingComponent.dueDate) }) : ""}`}
                                    </>
                                    : ""
                        }
                    </Typography>
                </>
            );
        }

        if (item.componentType == TrainingComponentType.Evaluation) {
            return <Typography variant="subtitle2" color="muted.light">
                {t("Student.EnrollmentView.Components.EvaluationSubtitle")}
            </Typography>;
        }
    }, [t]);

    const color: [MprcColors, MprcColorVariant] = item.failed
        ? ["error", "main"]
        : item.completed === true
            ? ["success", "main"]
            : (currentComponent?.componentId == item.componentId)
                ? ["highlight", "main"]
                : ["muted", item.enabled ? "main" : "light"];

    return (
        <Grid>
            <Stack direction="row" spacing={1}>
                <MprcText color={color}>
                    <FontAwesomeIcon
                        icon={
                            !item.computeProgress
                                ? faCircleMinus
                                : item.failed
                                    ? faCircleXmark
                                    : item.completed === true
                                        ? faCircleCheck
                                        : faCircle
                        }
                        fontSize={16}
                        fixedWidth
                        title={
                            !item.computeProgress
                                ? t("Student.EnrollmentView.NotCompteProgressTooltip")
                                : item.failed
                                    ? t("Student.EnrollmentView.ReprovedText")
                                    : item.completed === true
                                        ? t("Student.EnrollmentView.CompletedText")
                                        : !item.enabled
                                            ? t("Student.EnrollmentView.Components.NotEnabledText")
                                            : (currentComponent?.componentId == item.componentId || (!currentComponent && item.isCurrentComponent))
                                                ? t("Student.EnrollmentView.Components.InProgressText")
                                                : undefined}
                    />
                </MprcText>
                <div>
                    <Stack direction="row" spacing={1} alignItems="center">

                        <MprcClickable
                            onClick={() => onSelectComponent && onSelectComponent(item)}
                            disabled={!item.enabled}
                            title={BuildTitleTooltip(item)}
                            cursor={readonly ? "auto" : undefined}
                        >
                            <MprcTypography
                                noWrapRows={3}
                                component="p"
                                variant="subtitle1"
                                fontWeight={currentComponent?.componentId == item.componentId ? "bold" : undefined}
                                textColor={currentComponent?.componentId == item.componentId ? "highlight" : "muted"}
                                textColorVariant={item.enabled ? "main" : "light"}
                            >
                                {item.title}
                            </MprcTypography>
                        </MprcClickable>

                        {
                            item.composeFinalScore &&
                            <>
                                &nbsp;
                                <FontAwesomeIcon
                                    size="sm"
                                    fixedWidth
                                    icon={faCircleExclamationCheck}
                                    title={t("Student.EnrollmentView.Components.ComposeFinalScoreText")}
                                />
                            </>
                        }
                    </Stack>
                    <Stack direction="column" spacing={-0.5}>{BuildSubtitle(item)}</Stack>
                </div>
            </Stack>
        </Grid>
    );
};

////////////////////////////////////////////////////////////////////////////

export const EnrollmentComponents: React.FC<IProps> = (props) => {

    const { t } = useTranslation("lms");

    const { title, variant, currentComponent, items } = props;

    const [currentModule, setCurrentModule] = useState<number>(0);

    const modulesCount = [...new Set(items.map(i => i.groupNumber))].length;
    const moduleName = items.find(p => p.groupNumber == currentModule && p.componentType == TrainingComponentType.Phase)?.title ?? t("Student.EnrollmentView.ModulesText");

    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderItem = useCallback((item: EnrollmentComponentDto) => (
        <>
            {
                item.componentType == TrainingComponentType.Phase
                    ? <RenderPhase {...item} />
                    : item.componentType == TrainingComponentType.Sco && item.scoComponent!.hasChildren && !item.scoComponent!.isPage
                        ? <RenderScoGroup {...item} />
                        : <RenderTrainingComponent item={item} props={props} />
            }
        </>
    ), [t, currentComponent]);

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (currentComponent) setCurrentModule(currentComponent.groupNumber ?? 0);
    }, [currentComponent]);

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {variant == "vertical"
                ? (
                    <>
                        {
                            title &&
                            <MprcContainer>
                                <Typography variant="h3">{title}</Typography>
                            </MprcContainer>
                        }
                        <Grid container spacing={1}>
                            {
                                items.map((item, index) =>
                                    <Grid key={index} size={{ xs: 12 }}>
                                        <Grid container spacing={1} wrap="nowrap" alignItems="flex-start">
                                            {RenderItem(item)}
                                        </Grid>
                                    </Grid>
                                )
                            }
                        </Grid>
                    </>
                )
                : (
                    <>
                        <CardContent id="enrollmentComponenteHoritzontal" sx={{ display: "grid" }}>
                            <MprcContainer>
                                <Typography variant="h3">
                                    {moduleName}
                                </Typography>
                            </MprcContainer>
                            <Stack
                                direction="row"
                                spacing={2}
                                flexWrap="nowrap"
                                sx={{ overflowX: "auto" }}
                                pb={2}
                                className={AppGlobalClassKeys.thinScrollbar}
                            >
                                {
                                    items.filter(p => p.groupNumber == currentModule && p.componentType != TrainingComponentType.Phase)
                                        .map((item) =>
                                            <Grid key={item.componentId} container spacing={1} wrap="nowrap" justifyContent="center" alignItems="flex-start" minWidth={200} maxWidth={300}>
                                                {RenderItem(item)}
                                            </Grid>
                                        )
                                }
                            </Stack>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "center" }}>
                            {
                                variant == "horizontal" && modulesCount > 1 &&
                                <MobileStepper
                                    variant="dots"
                                    steps={modulesCount}
                                    position="static"
                                    activeStep={currentModule}
                                    sx={{ minWidth: 100 }}
                                    nextButton={
                                        <IconButton size="small" onClick={() => setCurrentModule(currentModule + 1)} disabled={currentModule === modulesCount - 1}>
                                            <FontAwesomeIcon icon={faAngleRight} />
                                        </IconButton>
                                    }
                                    backButton={
                                        <IconButton size="small" onClick={() => setCurrentModule(currentModule - 1)} disabled={currentModule === 0}>
                                            <FontAwesomeIcon icon={faAngleLeft} />
                                        </IconButton>
                                    }
                                />
                            }
                        </CardActions>
                    </>
                )
            }
        </>
    );
};
